import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  useTheme,
  useMediaQuery,
  Tooltip,
  CircularProgress,
  Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business,
  AccountCircle,
  Logout,
  Notifications,
  History,
  FileDownload,
  Archive,
  Settings as SettingsIcon,
  Receipt as ReceiptIcon,
  Sync as SyncIcon,
  CheckCircleOutline,
  ErrorOutline,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { animations } from '../../theme/theme';
import { apiService } from '../../utils/api';
import { useCacheBuster } from '../../hooks/useCacheBuster';
import LoadingDots from '../LoadingDots';
import Dashboard from '../Dashboard/Dashboard';
import PropertyTable from '../PropertyTable/PropertyTable';
import RequestsTable from '../RequestsTable/RequestsTable';
import AuditTrail from '../AuditTrail/AuditTrail';
import UserManagement from '../UserManagement/UserManagement';
import Export from '../Export/Export';
import Settings from '../Settings/Settings';
import Profile from '../Profile/Profile';
import useSafetyWatchdog from '../../hooks/useSafetyWatchdog';

const drawerWidth = 320;
const SYNC_TABLES = [
  'assessor_requests',
  'assessor_property_versions',
  'assessor_properties',
  'assessor_audit_trail'
];

const Layout = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallScreen = (() => {
    try {
      const w = window.innerWidth;
      const h = window.innerHeight;
      return (w <= 1280 && h <= 720) || (w <= 1366 && h <= 768) || (w <= 1920 && h <= 1080);
    } catch (_) {
      return false;
    }
  })();
  const computedDrawerWidth = isSmallScreen ? 300 : drawerWidth;
  const { user, logout, isSuperAdmin, isAdmin, isAssessor, isViewer } = useAuth();
  const canSyncData = isSuperAdmin || isAdmin || isAssessor;
  const initialSettings = (() => {
    if (typeof window !== 'undefined' && window.__ASSESSOR_SETTINGS__) return window.__ASSESSOR_SETTINGS__;
    try {
      const cached = localStorage.getItem('assessor_settings');
      if (cached) return JSON.parse(cached);
    } catch (_) {}
    const cachedLogo = typeof window !== 'undefined' ? localStorage.getItem('app_logo_url') : '';
    if (cachedLogo) return { app_logo_url: cachedLogo };
    return null;
  })();
  const [settings, setSettings] = useState(initialSettings);
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [syncAnchorEl, setSyncAnchorEl] = useState(null);
  const [isPushing, setIsPushing] = useState(false);
  const [isPulling, setIsPulling] = useState(false);
  const [syncStatusData, setSyncStatusData] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [syncConfig, setSyncConfig] = useState(null);
  const [syncConfigError, setSyncConfigError] = useState(null);
  const [currentPage, setCurrentPage] = useState(() => {
    // On reload, restore last page if saved; otherwise default to Dashboard
    try {
      const savedPage = localStorage.getItem('assessor_current_page');
      return savedPage || 'Dashboard';
    } catch (_) {
      return 'Dashboard';
    }
  });
  const [isPageAccessChecked, setIsPageAccessChecked] = useState(false);

  // Safety watchdog for access-check stage to avoid infinite spinner
  useSafetyWatchdog({
    isLoading: !isPageAccessChecked,
    isInitialLoad: !isPageAccessChecked,
    onTimeout: () => {
      try { console.warn('Layout: access check timed out. Proceeding to render.'); } catch (_) {}
      setIsPageAccessChecked(true);
    },
    timeoutMs: 15000,
    componentName: 'Layout',
    enabled: true
  });

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileClick = () => {
    setCurrentPage('Profile');
    setAnchorEl(null);
    setMobileOpen(false);
    try {
      localStorage.setItem('assessor_current_page', 'Profile');
    } catch (_) {
      // Ignore localStorage errors
    }
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSyncMenuOpen = (event) => {
    setSyncAnchorEl(event.currentTarget);
    setSyncResult(null);
  };

  const handleSyncMenuClose = () => {
    setSyncAnchorEl(null);
    setSyncResult(null);
  };

  const handlePush = async () => {
    if (!canSyncData || isPushing || isPulling) return;
    setSyncResult(null);
    setIsPushing(true);
    try {
      const response = await apiService.pushData(configuredSyncTables);
      setSyncResult({
        type: 'success',
        message: response?.message || 'Data pushed successfully.'
      });
      const [updatedStatus, configSummary] = await Promise.all([
        apiService.getSyncStatus(),
        apiService.getSyncConfig()
      ]);
      setSyncStatusData(updatedStatus);
      if (configSummary && !configSummary.code) {
        setSyncConfig(configSummary);
        setSyncConfigError(null);
      } else if (configSummary?.message) {
        setSyncConfigError(configSummary.message);
      }
    } catch (error) {
      setSyncResult({
        type: 'error',
        message: error?.message || 'Push failed. Please try again.'
      });
    } finally {
      setIsPushing(false);
    }
  };

  const handlePull = async () => {
    if (!canSyncData || isPushing || isPulling) return;
    setSyncResult(null);
    setIsPulling(true);
    try {
      const response = await apiService.pullData(configuredSyncTables);
      setSyncResult({
        type: 'success',
        message: response?.message || 'Data pulled successfully.'
      });
      const [updatedStatus, configSummary] = await Promise.all([
        apiService.getSyncStatus(),
        apiService.getSyncConfig()
      ]);
      setSyncStatusData(updatedStatus);
      if (configSummary && !configSummary.code) {
        setSyncConfig(configSummary);
        setSyncConfigError(null);
      } else if (configSummary?.message) {
        setSyncConfigError(configSummary.message);
      }
    } catch (error) {
      setSyncResult({
        type: 'error',
        message: error?.message || 'Pull failed. Please try again.'
      });
    } finally {
      setIsPulling(false);
    }
  };

  const handleLogout = async () => {
    // Clear the current page from localStorage when logging out
    try {
      localStorage.removeItem('assessor_current_page');
    } catch (_) {
      // Ignore localStorage errors
    }
    await logout();
  };

  const handleNavigation = (page) => {
    // Check if user has access to the requested page
    if (!canAccessPage(page)) {
      // Redirect to Dashboard if user doesn't have access
      setCurrentPage('Dashboard');
      setMobileOpen(false);
      try {
        localStorage.setItem('assessor_current_page', 'Dashboard');
      } catch (_) {
        // Ignore localStorage errors
      }
      return;
    }
    
    setCurrentPage(page);
    setMobileOpen(false);
    // Save the current page to localStorage for persistence across refreshes
    try {
      localStorage.setItem('assessor_current_page', page);
    } catch (_) {
      // Ignore localStorage errors
    }
  };

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await apiService.getSettings();
        setSettings(data);
        try {
          localStorage.setItem('assessor_settings', JSON.stringify(data));
          if (data && data.app_logo_url) localStorage.setItem('app_logo_url', data.app_logo_url);
        } catch (_) {}
      } catch (e) {
        setSettings(null);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (!canSyncData) return;
    let isMounted = true;
    const loadSyncDetails = async () => {
      try {
        const [status, configSummary] = await Promise.all([
          apiService.getSyncStatus(),
          apiService.getSyncConfig()
        ]);
        if (isMounted) {
          setSyncStatusData(status);
          if (configSummary && !configSummary.code) {
            setSyncConfig(configSummary);
            setSyncConfigError(null);
          } else if (configSummary?.message) {
            setSyncConfigError(configSummary.message);
          }
        }
      } catch (error) {
        if (isMounted) {
          setSyncResult({
            type: 'error',
            message: error?.message || 'Unable to load sync status.'
          });
          setSyncConfigError(error?.message || 'Unable to load sync configuration.');
        }
      }
    };
    loadSyncDetails();
    return () => {
      isMounted = false;
    };
  }, [canSyncData]);

  // Reset current page to Dashboard when user logs out
  useEffect(() => {
    if (!user) {
      setCurrentPage('Dashboard');
      setIsPageAccessChecked(false);
      try {
        localStorage.removeItem('assessor_current_page');
      } catch (_) {
        // Ignore localStorage errors
      }
    }
  }, [user]);

  // Helper function to check if user can access a specific page
  const canAccessPage = (pageName) => {
    const restrictedPages = ['Audit Trail', 'Export', 'User Management', 'Settings'];
    if (restrictedPages.includes(pageName)) {
      return isSuperAdmin || isAdmin || isAssessor;
    }
    return true; // All other pages are accessible
  };

  // Check if current page is accessible for current user role
  useEffect(() => {
    if (user && currentPage) {
      if (!canAccessPage(currentPage)) {
        // Redirect to Dashboard if user doesn't have access to current page
        setCurrentPage('Dashboard');
        try {
          localStorage.setItem('assessor_current_page', 'Dashboard');
        } catch (_) {
          // Ignore localStorage errors
        }
      }
      setIsPageAccessChecked(true);
    }
  }, [user, currentPage]);

  const navigationItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      badge: null,
      show: true // Always show Dashboard
    },
    {
      text: 'Properties',
      icon: <Business />,
      badge: null,
      show: true // Always show Properties
    },
    {
      text: 'Requests',
      icon: <ReceiptIcon />,
      badge: null,
      show: !isViewer, // Hide Requests for viewers
      disabled: false
    },
    {
      text: 'Audit Trail',
      icon: <History />,
      badge: null,
      show: canAccessPage('Audit Trail')
    },
    {
      text: 'Export',
      icon: <FileDownload />,
      badge: null,
      show: canAccessPage('Export')
    },
    {
      text: 'User Management',
      icon: <AccountCircle />,
      badge: null,
      show: canAccessPage('User Management')
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      badge: null,
      show: canAccessPage('Settings')
    }
  ];
  const syncMenuOpen = Boolean(syncAnchorEl);
  const syncEnv = syncStatusData?.env || {};
  const syncStatusSummary = syncStatusData?.status || {};
  const currentApiUrl = process.env.REACT_APP_API_URL || 'http://localhost/wp-json/assessor/v1';
  const productionApiUrl = syncEnv.production_api_url || syncConfig?.env?.production_api_url || process.env.REACT_APP_API_URL;
  const localApiUrl = syncEnv.local_api_url || syncConfig?.env?.local_api_url || 'http://localhost/wp-json/assessor/v1';
  const isProduction = useMemo(() => {
    if (!currentApiUrl || !productionApiUrl) return false;
    // Check if current URL matches production URL from sync config or env
    return currentApiUrl === productionApiUrl;
  }, [currentApiUrl, productionApiUrl]);
  const syncSourceUrl = isProduction ? localApiUrl : productionApiUrl;
  const syncTargetUrl = isProduction ? productionApiUrl : localApiUrl;
  const syncTableStats = Array.isArray(syncStatusSummary.tables) ? syncStatusSummary.tables : [];
  const configuredSyncTables = useMemo(() => {
    if (Array.isArray(syncConfig?.tables) && syncConfig.tables.length) {
      return syncConfig.tables;
    }
    return SYNC_TABLES;
  }, [syncConfig]);
  const syncRemoteInfo = syncConfig?.remote || {};
  const syncLocalInfo = syncConfig?.local || {};
  const pushLabel = isProduction ? 'Push to Local' : 'Push to Live';
  const pullLabel = isProduction ? 'Pull from Local' : 'Pull from Live';
  const formatSyncDate = useCallback((value) => {
    if (!value) return 'Never synced';
    try {
      const hasTimezone = /[zZ]|([+\-]\d{2}:?\d{2})$/.test(value);
      const normalized = hasTimezone ? value : `${value.replace(' ', 'T')}Z`;
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      }).format(new Date(normalized));
    } catch (_) {
      return value;
    }
  }, []);
  const lastSyncDisplay = formatSyncDate(syncStatusSummary.synced_at);
  const toFormalCase = (text) => {
    if (!text) return '';
    const small = new Set(['of','and','the','for','in','on','at','a','an']);
    const words = String(text).toLowerCase().split(/\s+/);
    return words.map((w, i) => {
      if (!w) return w;
      if (i > 0 && small.has(w)) return w;
      return w.charAt(0).toUpperCase() + w.slice(1);
    }).join(' ');
  };
  const headerPh = 'Republic of the Philippines';
  const baseProvince = (settings && settings.header_province) || (typeof window !== 'undefined' && window.__ASSESSOR_SETTINGS__ && window.__ASSESSOR_SETTINGS__.header_province) || 'Bukidnon';
  const headerProvince = `Province of ${toFormalCase(baseProvince)}`;
  const baseMunicipality = (settings && settings.header_municipality) || (typeof window !== 'undefined' && window.__ASSESSOR_SETTINGS__ && window.__ASSESSOR_SETTINGS__.header_municipality) || 'KITAOTAO';
  const headerMunicipality = `Municipality of ${toFormalCase(baseMunicipality)}`;
  const headerOffice = (settings && settings.header_office) || (typeof window !== 'undefined' && window.__ASSESSOR_SETTINGS__ && window.__ASSESSOR_SETTINGS__.header_office) || 'OFFICE OF THE MUNICIPAL ASSESSOR';


  const formatRole = (role) => {
    if (!role || typeof role !== 'string') return '';
    const normalized = role.trim().toLowerCase();
    if (normalized === 'assessor') return 'Municipal Assessor';
    if (normalized === 'municipal assessor') return 'Municipal Assessor';
    if (normalized === 'administrator') return 'Administrator';
    if (normalized === 'admin') return 'Administrator';
    if (normalized === 'superadmin') return 'Super Admininstrator';
    return role
      .split(/\s+/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const drawer = (
    <Box>
      <Box sx={{ 
        py: isSmallScreen ? 3 : 4, 
        px: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: isSmallScreen ? 1.5 : 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {settings?.app_logo_url ? (
            <img src={settings.app_logo_url} alt="Logo" style={{ maxHeight: isSmallScreen ? 48 : 64, display: 'block' }} />
          ) : (
            <Business sx={{ fontSize: isSmallScreen ? 22 : 28, color: 'primary.main' }} />
          )}
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="body2" fontWeight={600} sx={{ fontSize: isSmallScreen ? '0.9rem' : undefined }}>
            Assessor's Archiving System
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.85rem' : undefined }}>
            {headerMunicipality}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: isSmallScreen ? '0.75rem' : undefined }}>
            {headerProvince}
          </Typography>
        </Box>
      </Box>
      
      <List>
        {navigationItems.filter(item => item.show).map((item) => (
          <motion.div key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                  onClick={() => handleNavigation(item.text)}
                  selected={currentPage === item.text}
                  sx={{
                      display: 'flex',
                      alignItems: 'center',
                      position: 'relative',
                      borderRadius: 0,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        color: 'primary.main',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: 4,
                          backgroundColor: 'primary.main',
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)',
                          color: 'primary.main',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                >
                  <ListItemIcon
                   sx={{
                     color: currentPage === item.text ? 'primary.main' : 'text.secondary',
                     minWidth: 40,
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                   }}
                 >
                  {item.icon}
                </ListItemIcon>
                                 <ListItemText 
                   primary={item.text}
                   primaryTypographyProps={{
                     fontWeight: currentPage === item.text ? 600 : 400,
                     color: currentPage === item.text ? 'primary.main' : 'inherit',
                   }}
                   sx={{
                     marginLeft: 0,
                   }}
                 />
                {item.badge && (
                  <Badge badgeContent={item.badge} color="error" />
                )}
              </ListItemButton>
            </ListItem>
          </motion.div>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
        {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${computedDrawerWidth}px)` },
          ml: { md: `${computedDrawerWidth}px` },
          background: '#f8fafc',
          color: '#475569',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
        }}
      >
        <Toolbar sx={{ alignItems: 'center' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, display: 'flex' }}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              {currentPage === 'Dashboard' && <DashboardIcon sx={{ fontSize: 24, color: 'primary.main', verticalAlign: 'middle' }} />}
              {currentPage === 'Properties' && <Business sx={{ fontSize: 24, color: 'primary.main', verticalAlign: 'middle' }} />}
              {currentPage === 'Requests' && <ReceiptIcon sx={{ fontSize: 24, color: 'primary.main', verticalAlign: 'middle' }} />}
              {currentPage === 'Audit Trail' && <History sx={{ fontSize: 24, color: 'primary.main', verticalAlign: 'middle' }} />}
              {currentPage === 'Export' && <FileDownload sx={{ fontSize: 24, color: 'primary.main', verticalAlign: 'middle' }} />}
              {currentPage === 'User Management' && <AccountCircle sx={{ fontSize: 24, color: 'primary.main', verticalAlign: 'middle' }} />}
              {currentPage === 'Settings' && <SettingsIcon sx={{ fontSize: 24, color: 'primary.main', verticalAlign: 'middle' }} />}
              {currentPage === 'Profile' && <AccountCircle sx={{ fontSize: 24, color: 'primary.main', verticalAlign: 'middle' }} />}
              <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1, display: 'inline-flex', alignItems: 'center' }}>
                {currentPage}
              </Typography>
            </motion.div>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Notifications">
              <IconButton color="inherit" size="small">
                <Badge badgeContent={0} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>
            {canSyncData && (
              <Tooltip title="Sync data between environments">
                <IconButton
                  color="inherit"
                  size="small"
                  onClick={handleSyncMenuOpen}
                >
                  {(isPushing || isPulling) ? (
                    <CircularProgress size={18} thickness={5} color="inherit" />
                  ) : (
                    <SyncIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            
            <Tooltip title="Profile">
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{ ml: 1 }}
              >
                <Avatar
                  src={user?.avatar_url || undefined}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    bgcolor: user?.avatar_url ? 'transparent' : 'primary.main',
                    fontSize: '0.875rem'
                  }}
                >
                  {!user?.avatar_url && (user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U')}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: computedDrawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: computedDrawerWidth,
              background: '#ffffff',
              borderRight: `1px solid ${theme.palette.divider}`
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: computedDrawerWidth,
              background: '#ffffff',
              borderRight: `1px solid ${theme.palette.divider}`
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${computedDrawerWidth}px)` },
          mt: '64px',
          background: '#f8fafc',
          minHeight: 'calc(100vh - 64px)',
          // Hide scrollbar but maintain scrolling functionality
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          // For Firefox
          scrollbarWidth: 'none',
          // For IE and Edge
          msOverflowStyle: 'none',
          // Ensure scrolling still works
          overflow: 'auto'
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentPage}
            initial="initial"
            animate="animate"
            exit="exit"
            variants={animations.fadeIn}
          >
            {!isPageAccessChecked ? (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '70vh',
                gap: 2
              }}>
                <CircularProgress size={50} thickness={4} />
                <Typography variant="h6" color="text.secondary">
                  Loading<LoadingDots />
                </Typography>
                {/* <Typography variant="body2" color="text.secondary">
                  Please wait while the system loads
                </Typography> */}
              </Box>
            ) : (
              <>
                {currentPage === 'Dashboard' && <Dashboard onNavigate={handleNavigation} />}
                {currentPage === 'Properties' && <PropertyTable />}
                {currentPage === 'Requests' && <RequestsTable />}
                {currentPage === 'Audit Trail' && canAccessPage('Audit Trail') ? <AuditTrail /> : 
                  currentPage === 'Audit Trail' && <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h5" color="text.secondary" gutterBottom>Access Denied</Typography>
                    <Typography variant="body1" color="text.secondary">You don't have permission to access this page.</Typography>
                  </Box>}
                {currentPage === 'Export' && canAccessPage('Export') ? <Export /> : 
                  currentPage === 'Export' && <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h5" color="text.secondary" gutterBottom>Access Denied</Typography>
                    <Typography variant="body1" color="text.secondary">You don't have permission to access this page.</Typography>
                  </Box>}
                {currentPage === 'User Management' && canAccessPage('User Management') ? <UserManagement /> : 
                  currentPage === 'User Management' && <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h5" color="text.secondary" gutterBottom>Access Denied</Typography>
                    <Typography variant="body1" color="text.secondary">You don't have permission to access this page.</Typography>
                  </Box>}
                {currentPage === 'Settings' && canAccessPage('Settings') ? <Settings /> : 
                  currentPage === 'Settings' && <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography variant="h5" color="text.secondary" gutterBottom>Access Denied</Typography>
                    <Typography variant="body1" color="text.secondary">You don't have permission to access this page.</Typography>
                  </Box>}
                {currentPage === 'Profile' && <Profile />}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Profile Menu */}
      {canSyncData && (
        <Menu
          anchorEl={syncAnchorEl}
          open={syncMenuOpen}
          onClose={handleSyncMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              mt: 1,
              minWidth: 320,
              maxWidth: 360,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              borderRadius: 4,
              p: 0
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Data Synchronization
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isProduction ? 'Sync data between production and local' : 'Sync data between local and production'}
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Current Environment
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {currentApiUrl || 'Unknown'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isProduction ? 'Production' : 'Local Development'}
              </Typography>
            </Box>
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {isProduction ? 'Local Environment' : 'Production Environment'}
              </Typography>
              <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                {isProduction ? localApiUrl : productionApiUrl}
              </Typography>
              {isProduction ? (
                (syncLocalInfo?.host || syncLocalInfo?.database) && (
                  <Typography variant="caption" color="text.secondary">
                    {syncLocalInfo.host || 'local'}{syncLocalInfo.database ? ` · ${syncLocalInfo.database}` : ''}
                  </Typography>
                )
              ) : (
                syncRemoteInfo?.host && (
                  <Typography variant="caption" color="text.secondary">
                    {syncRemoteInfo.host}{syncRemoteInfo.database ? ` · ${syncRemoteInfo.database}` : ''}
                  </Typography>
                )
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Last sync
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {lastSyncDisplay}
              </Typography>
            </Box>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Tables
              </Typography>
              <Box sx={{ mt: 1, maxHeight: 180, overflowY: 'auto' }}>
                {(syncTableStats.length ? syncTableStats : configuredSyncTables.map((name) => ({ table: name, row_count: null }))).map((entry) => (
                  <Box
                    key={entry.table}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.5
                    }}
                  >
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {entry.table}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.row_count !== null && entry.row_count !== undefined ? `${entry.row_count} rows` : 'pending'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            {syncConfigError && (
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: 'warning.main'
                }}
              >
                <ErrorOutline fontSize="small" />
                <Typography variant="body2">{syncConfigError}</Typography>
              </Box>
            )}
            {syncResult && (
              <Box
                sx={{
                  mt: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  color: syncResult.type === 'error' ? 'error.main' : 'success.main'
                }}
              >
                {syncResult.type === 'error' ? (
                  <ErrorOutline fontSize="small" />
                ) : (
                  <CheckCircleOutline fontSize="small" />
                )}
                <Typography variant="body2">{syncResult.message}</Typography>
              </Box>
            )}
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                fullWidth
                size="small"
                onClick={handlePush}
                disabled={isPushing || isPulling}
                startIcon={isPushing ? <CircularProgress size={16} thickness={5} color="inherit" /> : <ArrowUpward fontSize="small" />}
                sx={{ flex: 1 }}
              >
                {isPushing ? 'Pushing...' : pushLabel}
              </Button>
              <Button
                variant="outlined"
                fullWidth
                size="small"
                onClick={handlePull}
                disabled={isPushing || isPulling}
                startIcon={isPulling ? <CircularProgress size={16} thickness={5} color="inherit" /> : <ArrowDownward fontSize="small" />}
                sx={{ flex: 1 }}
              >
                {isPulling ? 'Pulling...' : pullLabel}
              </Button>
            </Box>
          </Box>
        </Menu>
      )}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            borderRadius: 4
          }
        }}
      >
        <Box sx={{ padding: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {user?.full_name || user?.username}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {formatRole(user?.role)}
          </Typography>
        </Box>
        
        <MenuItem onClick={handleProfileClick}>
          <ListItemIcon>
            <AccountCircle fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        
        <Divider />
        
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Layout;

